function initConceptoraHome(section) {
  section._conceptoraHomeAbort?.abort();
  const controller = new AbortController();
  section._conceptoraHomeAbort = controller;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const strip = section.querySelector('[data-library-strip]');
  const previous = section.querySelector('[data-library-previous]');
  const next = section.querySelector('[data-library-next]');
  const libraryStatus = section.querySelector('[data-library-status]');
  if (strip && previous && next) {
    const librarySection = strip.closest('.conceptora-home__libraries');
    const cards = [...strip.querySelectorAll('.conceptora-library-card')];
    let libraryScrollFrame = 0;
    let activeLibraryIndex = -1;
    let dragStartX = 0;
    let dragStartLeft = 0;
    let dragging = false;
    let pointerDown = false;
    let suppressClick = false;
    const DRAG_CLICK_THRESHOLD = 12;
    librarySection?.classList.add('is-enhanced');
    const canScroll = () => strip.scrollWidth > strip.clientWidth + 2;
    const atEnd = () => canScroll() && strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
    const closestCardIndex = () => {
      if (!cards.length) return 0;
      if (!canScroll()) return 0;
      if (atEnd()) return cards.length - 1;
      const targetLeft = strip.scrollLeft + cards[0].offsetLeft;
      let closest = 0;
      cards.forEach((card, index) => {
        if (Math.abs(card.offsetLeft - targetLeft) < Math.abs(cards[closest].offsetLeft - targetLeft)) closest = index;
      });
      return closest;
    };
    const update = () => {
      const activeIndex = closestCardIndex();
      previous.disabled = strip.scrollLeft <= 2;
      next.disabled = !canScroll() || atEnd();
      cards.forEach((card, index) => card.classList.toggle('is-active', index === activeIndex));
      if (libraryStatus && cards.length && activeIndex !== activeLibraryIndex) {
        activeLibraryIndex = activeIndex;
        libraryStatus.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      }
    };
    const move = (direction) => {
      if (!cards.length || !canScroll()) return;
      const target = Math.max(0, Math.min(cards.length - 1, closestCardIndex() + direction));
      const cardStep = cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].getBoundingClientRect().width;
      const targetLeft = direction < 0 && atEnd()
        ? Math.max(0, strip.scrollLeft - cardStep)
        : cards[target].offsetLeft - cards[0].offsetLeft;
      strip.scrollTo({ left: targetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    previous.addEventListener('click', () => move(-1), { signal: controller.signal });
    next.addEventListener('click', () => move(1), { signal: controller.signal });
    strip.addEventListener('scroll', () => {
      if (libraryScrollFrame) return;
      libraryScrollFrame = requestAnimationFrame(() => {
        libraryScrollFrame = 0;
        update();
      });
    }, { passive: true, signal: controller.signal });
    window.addEventListener('resize', update, { signal: controller.signal });
    strip.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragStartX = event.clientX;
      dragStartLeft = strip.scrollLeft;
      dragging = false;
      pointerDown = true;
      suppressClick = false;
    }, { signal: controller.signal });
    strip.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;
      const distance = event.clientX - dragStartX;
      if (Math.abs(distance) > DRAG_CLICK_THRESHOLD) {
        if (!dragging) {
          dragging = true;
          strip.setPointerCapture(event.pointerId);
          strip.classList.add('is-dragging');
        }
      }
      if (!dragging) return;
      strip.scrollLeft = dragStartLeft - distance;
    }, { signal: controller.signal });
    const endDrag = (event) => {
      pointerDown = false;
      if (!strip.hasPointerCapture(event.pointerId)) return;
      suppressClick = dragging;
      strip.releasePointerCapture(event.pointerId);
      strip.classList.remove('is-dragging');
      dragging = false;
    };
    strip.addEventListener('pointerup', endDrag, { signal: controller.signal });
    strip.addEventListener('pointercancel', endDrag, { signal: controller.signal });
    strip.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      suppressClick = false;
    }, { capture: true, signal: controller.signal });
    cards.forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch' || reducedMotion.matches || window.innerWidth < 990) return;
        const image = card.querySelector('img');
        if (!image) return;
        const bounds = card.getBoundingClientRect();
        image.style.setProperty('--card-image-x', `${(((event.clientX - bounds.left) / bounds.width) - 0.5) * 8}px`);
        image.style.setProperty('--card-image-y', `${(((event.clientY - bounds.top) / bounds.height) - 0.5) * 8}px`);
      }, { signal: controller.signal });
      card.addEventListener('pointerleave', () => {
        const image = card.querySelector('img');
        image?.style.removeProperty('--card-image-x');
        image?.style.removeProperty('--card-image-y');
      }, { signal: controller.signal });
    });
    controller.signal.addEventListener('abort', () => {
      if (libraryScrollFrame) cancelAnimationFrame(libraryScrollFrame);
    }, { once: true });
    update();
  }

  const insideSection = section.querySelector('[data-inside-section]');
  const insideItems = insideSection ? [...insideSection.querySelectorAll('[data-inside-item]')] : [];
  const insideTriggers = insideSection ? [...insideSection.querySelectorAll('[data-inside-trigger]')] : [];
  const insidePreviewTitle = insideSection?.querySelector('[data-inside-preview-title]');
  const insidePreviewCopy = insideSection?.querySelector('[data-inside-preview-copy]');
  const insidePreviewStage = insideSection?.querySelector('[data-inside-preview-stage]');
  const insidePreviewItems = insideSection?.querySelector('[data-inside-preview-items]');
  const insidePreviewLink = insideSection?.querySelector('[data-inside-preview-link]');
  if (insideSection && insideItems.length && insideTriggers.length && insidePreviewTitle && insidePreviewCopy && insidePreviewStage && insidePreviewItems && insidePreviewLink) {
    insideSection.classList.add('is-enhanced');
    const activateInside = (index, focus = false) => {
      insideItems.forEach((insideItem, itemIndex) => {
        const active = itemIndex === index;
        insideItem.open = active;
        if (active) insideTriggers[itemIndex].setAttribute('aria-current', 'true');
        else insideTriggers[itemIndex].removeAttribute('aria-current');
      });
      insidePreviewTitle.textContent = insideTriggers[index].querySelector('.conceptora-home__inside-title')?.textContent || '';
      insidePreviewCopy.textContent = insideItems[index].querySelector('[data-inside-copy]')?.textContent || '';
      const stage = insideItems[index].querySelector('[data-inside-stage]')?.textContent?.trim() || '';
      insidePreviewStage.textContent = stage;
      insidePreviewStage.hidden = !stage;
      const previewItems = [...insideItems[index].querySelectorAll('[data-inside-items] li')].map((item) => item.textContent?.trim()).filter(Boolean);
      insidePreviewItems.replaceChildren(...previewItems.map((item) => {
        const listItem = document.createElement('li');
        listItem.textContent = item;
        return listItem;
      }));
      insidePreviewItems.hidden = previewItems.length === 0;
      const sourceLink = insideItems[index].querySelector('[data-inside-link]');
      const href = sourceLink?.getAttribute('href') || '';
      const label = sourceLink?.textContent?.replace('→', '').trim() || '';
      insidePreviewLink.href = href;
      insidePreviewLink.firstChild.textContent = label;
      insidePreviewLink.hidden = !href || !label;
      if (focus) insideTriggers[index].focus();
    };
    insideTriggers.forEach((trigger, index) => {
      trigger.addEventListener('focus', () => activateInside(index), { signal: controller.signal });
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        activateInside(index);
      }, { signal: controller.signal });
      trigger.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') activateInside(0, true);
        else if (event.key === 'End') activateInside(insideTriggers.length - 1, true);
        else activateInside((index + (event.key === 'ArrowRight' ? 1 : -1) + insideTriggers.length) % insideTriggers.length, true);
      }, { signal: controller.signal });
    });
    activateInside(Math.max(0, insideTriggers.findIndex((trigger) => trigger.getAttribute('aria-current') === 'true')));
  }

  const chapterNav = section.querySelector('[data-chapter-nav]');
  const chapterLinks = chapterNav ? [...chapterNav.querySelectorAll('[data-chapter-link]')] : [];
  const chapterSections = [...section.querySelectorAll('[data-chapter]')];
  if (chapterNav && chapterLinks.length && chapterSections.length) {
    const targets = chapterLinks.map((link) => document.getElementById(link.hash.slice(1)));
    const updateChapter = () => {
      const sectionBounds = section.getBoundingClientRect();
      chapterNav.classList.toggle('is-visible', sectionBounds.top < window.innerHeight * 0.7 && sectionBounds.bottom > window.innerHeight * 0.3);
      let activeIndex = 0;
      targets.forEach((target, index) => {
        if (target && target.getBoundingClientRect().top <= window.innerHeight * 0.38) activeIndex = index;
      });
      chapterLinks.forEach((link, index) => {
        if (index === activeIndex) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    window.addEventListener('scroll', updateChapter, { passive: true, signal: controller.signal });
    window.addEventListener('resize', updateChapter, { signal: controller.signal });
    updateChapter();
  }

  const hero = section.querySelector('[data-conceptora-hero]');
  const planet = section.querySelector('[data-hero-planet]');
  const nebulaVideo = section.querySelector('[data-nebula-video]');
  const layers = [...section.querySelectorAll('[data-hero-layer]')];
  const tabs = [...section.querySelectorAll('[data-system-tab]')];
  const panels = [...section.querySelectorAll('[data-system-panel]')];
  const systemGrid = section.querySelector('[data-system-grid]');
  const systemPrevious = section.querySelector('[data-system-previous]');
  const systemNext = section.querySelector('[data-system-next]');
  const systemStatus = section.querySelector('[data-system-status]');
  if (!hero || !planet || !tabs.length) return;
  const motionEnabled = hero.dataset.nebulaMotion !== 'false';
  let visible = true;
  let swipeStart = 0;
  let pointerFrame = 0;
  let systemScrollFrame = 0;
  let pendingPointer = null;
  let videoReady = false;
  let videoRequested = false;

  const prefersDataSaving = () => navigator.connection?.saveData === true;
  const canLoadVideo = () => window.innerWidth >= 990 && !prefersDataSaving() && !reducedMotion.matches;

  const showStaticNebula = (reset = false) => {
    if (!nebulaVideo) return;
    nebulaVideo.pause();
    hero.classList.remove('is-nebula-video-ready');
    if (reset) nebulaVideo.currentTime = 0;
  };

  const updateVideoPlayback = (active) => {
    if (!nebulaVideo) return;
    if (!active || !canLoadVideo()) {
      showStaticNebula(!motionEnabled || reducedMotion.matches || prefersDataSaving());
      return;
    }
    if (!videoRequested) {
      videoRequested = true;
      nebulaVideo.load();
    }
    if (!videoReady) return;
    const playRequest = nebulaVideo.play();
    if (playRequest) {
      playRequest.then(() => hero.classList.add('is-nebula-video-ready')).catch(() => showStaticNebula());
    } else {
      hero.classList.add('is-nebula-video-ready');
    }
  };

  const clearPointerMotion = () => {
    if (pointerFrame) cancelAnimationFrame(pointerFrame);
    pointerFrame = 0;
    pendingPointer = null;
    layers.forEach((layer) => {
      layer.style.removeProperty('--layer-x');
      layer.style.removeProperty('--layer-y');
      layer.style.removeProperty('--layer-rotate');
    });
  };

  const updateMotion = () => {
    const active = motionEnabled && visible && !reducedMotion.matches && !document.hidden;
    hero.classList.toggle('is-motion-ready', active);
    updateVideoPlayback(active);
    if (!active) clearPointerMotion();
  };

  if (nebulaVideo) {
    const markVideoReady = () => {
      videoReady = true;
      updateMotion();
    };
    nebulaVideo.addEventListener('loadedmetadata', markVideoReady, { signal: controller.signal });
    nebulaVideo.addEventListener('loadeddata', markVideoReady, { signal: controller.signal });
    if (nebulaVideo.readyState >= 1) markVideoReady();
    nebulaVideo.addEventListener('error', () => {
      videoReady = false;
      showStaticNebula(true);
    }, { signal: controller.signal });
  }

  const updateSystemNavigation = (index) => {
    if (systemStatus) systemStatus.textContent = `${String(index + 1).padStart(2, '0')} / ${String(tabs.length).padStart(2, '0')}`;
    if (systemPrevious) systemPrevious.disabled = index === 0;
    if (systemNext) systemNext.disabled = index === tabs.length - 1;
  };

  const activate = (index, focus = false) => {
    const normalized = (index + tabs.length) % tabs.length;
    tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === normalized;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel, panelIndex) => panel.classList.toggle('is-active', panelIndex === normalized));
    updateSystemNavigation(normalized);
    if (focus) tabs[normalized].focus();
  };

  const scrollSystemTo = (index) => {
    const firstPanel = panels[0];
    const panel = panels[index];
    if (!systemGrid || !firstPanel || !panel) return;
    systemGrid.scrollTo({
      left: panel.offsetLeft - firstPanel.offsetLeft,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activate(index), { signal: controller.signal });
    tab.addEventListener('keydown', (event) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') activate(0, true);
      else if (event.key === 'End') activate(tabs.length - 1, true);
      else activate(index + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, { signal: controller.signal });
  });

  if (systemGrid && systemPrevious && systemNext) {
    const moveSystem = (direction) => {
      const active = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const target = Math.max(0, Math.min(tabs.length - 1, active + direction));
      activate(target);
      scrollSystemTo(target);
    };
    systemPrevious.addEventListener('click', () => moveSystem(-1), { signal: controller.signal });
    systemNext.addEventListener('click', () => moveSystem(1), { signal: controller.signal });
    systemGrid.addEventListener('scroll', () => {
      if (systemScrollFrame) return;
      systemScrollFrame = requestAnimationFrame(() => {
        systemScrollFrame = 0;
        const firstPanel = panels[0];
        if (!firstPanel) return;
        const targetLeft = systemGrid.scrollLeft + firstPanel.offsetLeft;
        let closest = 0;
        panels.forEach((panel, index) => {
          if (Math.abs(panel.offsetLeft - targetLeft) < Math.abs(panels[closest].offsetLeft - targetLeft)) closest = index;
        });
        activate(closest);
      });
    }, { passive: true, signal: controller.signal });
  }
  activate(Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));

  hero.addEventListener('pointermove', (event) => {
    if (!motionEnabled || reducedMotion.matches || !visible || document.hidden || event.pointerType === 'touch') return;
    const bounds = hero.getBoundingClientRect();
    pendingPointer = {
      x: ((event.clientX - bounds.left) / bounds.width - 0.5) * 2,
      y: ((event.clientY - bounds.top) / bounds.height - 0.5) * 2,
    };
    if (pointerFrame) return;
    pointerFrame = requestAnimationFrame(() => {
      const position = pendingPointer;
      pointerFrame = 0;
      if (!position) return;
      const depth = { 'ring-back': 5, core: 4, near: 10 };
      layers.forEach((layer) => {
        const amount = depth[layer.dataset.heroLayer] || 4;
        layer.style.setProperty('--layer-x', `${position.x * amount}px`);
        layer.style.setProperty('--layer-y', `${position.y * amount}px`);
        if (layer.dataset.heroLayer === 'ring-back') {
          layer.style.setProperty('--layer-rotate', `${position.x * 0.45}deg`);
        }
      });
    });
  }, { signal: controller.signal });
  hero.addEventListener('pointerleave', clearPointerMotion, { signal: controller.signal });
  planet.addEventListener('pointerdown', (event) => { swipeStart = event.clientX; }, { signal: controller.signal });
  planet.addEventListener('pointerup', (event) => {
    const distance = event.clientX - swipeStart;
    if (Math.abs(distance) < 60) return;
    const active = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    activate(active + (distance < 0 ? 1 : -1));
  }, { signal: controller.signal });

  const observer = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    updateMotion();
  }, { threshold: 0.1 });
  observer.observe(hero);
  document.addEventListener('visibilitychange', updateMotion, { signal: controller.signal });
  reducedMotion.addEventListener('change', updateMotion, { signal: controller.signal });
  navigator.connection?.addEventListener?.('change', updateMotion, { signal: controller.signal });
  window.addEventListener('resize', updateMotion, { signal: controller.signal });
  controller.signal.addEventListener('abort', () => {
    observer.disconnect();
    if (systemScrollFrame) cancelAnimationFrame(systemScrollFrame);
    showStaticNebula();
    clearPointerMotion();
  }, { once: true });
}

document.querySelectorAll('.conceptora-home').forEach(initConceptoraHome);
if (!window.__conceptoraHomeEditorListener) {
  window.__conceptoraHomeEditorListener = true;
  const conceptoraHomesWithin = (target) => {
    const homes = [...target.querySelectorAll('.conceptora-home')];
    if (target.matches?.('.conceptora-home')) homes.unshift(target);
    return homes;
  };
  document.addEventListener('shopify:section:load', (event) => {
    conceptoraHomesWithin(event.target).forEach(initConceptoraHome);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    conceptoraHomesWithin(event.target).forEach((home) => home._conceptoraHomeAbort?.abort());
  });
}
